import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Title from "../common/Title";
import { RiDashboard3Line, RiDraggable } from "react-icons/ri";
import { MdWorkspacesOutline } from "react-icons/md";
import KPIs from "./KPIs";
import LatestExecutions from "./LatestExecutions";
import NextExecutions from "./NextExecutions";
import FavoriteRunbooks from "./FavoriteRunbooks";
import { Row, Col, Button, Alert } from "react-bootstrap";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../contexts/TourContext";
import { useWorkspaceExperience } from "../../contexts/WorkspaceExperienceContext";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableItem({ id, children, isMobile, hideOnMobile }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isMobile });

  const style = isMobile ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? "grabbing" : "grab",
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <Col md={6} className={`mb-3 ${hideOnMobile ? 'd-none d-md-block' : ''}`}>
      <div ref={setNodeRef} style={style}>
        {!isMobile && (
          <div
            {...attributes}
            {...listeners}
            className="drag-handle"
            title="Drag to reorder"
          >
            <RiDraggable className="drag-handle-icon" />
          </div>
        )}
        <div style={{ pointerEvents: "auto" }}>
          {children}
        </div>
      </div>
    </Col>
  );
}

// Once dismissed, stays dismissed — this is a one-time nudge, not a recurring interruption.
const WORKSPACE_BANNER_DISMISSED_KEY = "workspaceExperienceBannerDismissed";

function Dashboard(props) {
  const navigate = useNavigate();
  const { activeTour, startTour, endTour, isTourCompleted } = useTour();
  const { workspaceExperienceEnabled, setWorkspaceExperienceEnabled } = useWorkspaceExperience();
  const [runTour, setRunTour] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem(WORKSPACE_BANNER_DISMISSED_KEY) === "true"
  );

  const handleDismissWorkspaceBanner = () => {
    localStorage.setItem(WORKSPACE_BANNER_DISMISSED_KEY, "true");
    setBannerDismissed(true);
  };

  const handleTryWorkspace = () => {
    setWorkspaceExperienceEnabled(true);
    navigate("/workspace");
  };

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const componentMap = {
    latest: LatestExecutions,
    next: NextExecutions,
    kpis: KPIs,
    favorites: FavoriteRunbooks,
  };

  const defaultLayout = ["latest", "next", "favorites", "kpis"];

  const [layout, setLayout] = useState(() => {
    const saved = localStorage.getItem("dashboardLayout");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
          // Filter out analytics components that have been moved to separate page
          const filtered = parsed.filter(item => item !== 'analyticsOverview' && item !== 'apiPerformance');
          return filtered.length > 0 ? filtered : defaultLayout;
        }
      } catch (e) {
        localStorage.removeItem("dashboardLayout");
      }
    }
    return defaultLayout;
  });

  useEffect(() => {
    localStorage.setItem("dashboardLayout", JSON.stringify(layout));
  }, [layout]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLayout((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleResetDashboard = () => {
    localStorage.removeItem("dashboardLayout");
    setLayout(defaultLayout);
  };

  useEffect(() => {
    if (activeTour === "dashboard") {
      setRunTour(true);
    }
  }, [activeTour]);

  const tourSteps = [
    {
      target: "body",
      content: "Welcome to the Dashboard! Let me show you around the portal.",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="sidebar-menu"]',
      content: "Use this sidebar to navigate between different sections: Dashboard, Analytics, Runbooks, Schedulers, Variables, and Connectors.",
    },
    {
      target: ".dashboard-title",
      content: "This is your dashboard where you can see an overview of your runbook executions and quick access to favorite runbooks.",
    },
    {
      target: '[data-tour="latest-executions"]',
      content: "Here you can see the latest runbook executions with their status. This updates automatically every minute.",
    },
    {
      target: '[data-tour="next-executions"]',
      content: "This shows upcoming scheduled runbook executions. Also auto-refreshes every minute.",
    },
    {
      target: '[data-tour="favorites"]',
      content: "Your favorite runbooks appear here for quick access. Click Execute to run them or History to see past executions.",
    },
    {
      target: '[data-tour="kpis"]',
      content: "This shows the average execution time for your runbooks over the last month.",
    },
    {
      target: ".dashboard-reset",
      content: "You can drag and drop the dashboard widgets to reorder them. Use this button to reset to the default layout.",
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      endTour("dashboard");
    }
  };

  return (
    <>
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: "#cf7f00",
            textColor: "#334155",
            backgroundColor: "#ffffff",
            arrowColor: "#ffffff",
            zIndex: 10000,
          },
          buttonNext: {
            backgroundColor: "#cf7f00",
            borderRadius: "0.5rem",
            fontSize: "14px",
            padding: "10px 20px",
          },
          buttonBack: {
            color: "#cf7f00",
            marginRight: "10px",
          },
          buttonSkip: {
            color: "#64748b",
          },
          tooltip: {
            borderRadius: "0.75rem",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          },
          tooltipContent: {
            padding: "20px",
          },
        }}
      />
      {(props.user?.isSuperUser || props.user?.isProductTeam) && !workspaceExperienceEnabled && !bannerDismissed && (
        <Alert
          variant="info"
          dismissible
          onClose={handleDismissWorkspaceBanner}
          className="d-flex align-items-center justify-content-between flex-wrap gap-2"
        >
          <div>
            <MdWorkspacesOutline className="me-2 mb-1" size={20} />
            <strong>Try our new Workspace experience</strong> — My Runbooks, My Schedulers, My
            Variables, My Connectors, and Administration open as floating windows you can
            arrange, resize, and pop out side by side.
          </div>
          <Button variant="primary" size="sm" onClick={handleTryWorkspace}>
            Try it now
          </Button>
        </Alert>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Title text="Dashboard" icon={<RiDashboard3Line className="mb-1" />} className="dashboard-title" />
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleResetDashboard}
          className="dashboard-reset d-none d-md-inline-block"
        >
          Reset Dashboard
        </Button>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={layout} strategy={rectSortingStrategy}>
          <Row>
            {layout.map((id) => {
              const Component = componentMap[id];
              // Hide KPIs and Favorites on mobile
              const hideOnMobile = (id === 'kpis' || id === 'favorites');
              return (
                <SortableItem key={id} id={id} isMobile={isMobile} hideOnMobile={hideOnMobile}>
                  <Component user={props.user} />
                </SortableItem>
              );
            })}
          </Row>
        </SortableContext>
      </DndContext>
    </>
  );
}

export default Dashboard;
